import type { ReactNode } from "react";
import type { ImageSourcePropType } from "react-native";
import { Box } from "../../ui/box";
import { HStack } from "../../ui/hstack";
import { Image } from "../../ui/image";
import { Text } from "../../ui/text";
import { VStack } from "../../ui/vstack";
import MontuEmblem from "./MontuEmblem";

type Props = {
  /** The 3D "glass" illustration this moment is built around. Optional so the
   * chat kit still renders (icon-less) for a consumer that has not supplied
   * `AgentIllustrations` — the kit itself ships no image assets. */
  image?: ImageSourcePropType;
  /** A second, smaller glass illustration pinned to the main one's bottom-right
   * corner — e.g. `search.png` on `package.png`, `clock.png` on `card.png`. */
  badgeImage?: ImageSourcePropType;
  /** Bobs the icon and shimmers the title while the moment is still live; both go
   * still once it settles. */
  active: boolean;
  title: string;
  children?: ReactNode;
};

/**
 * The shell for animation system v2's one full-width "moment" card.
 *
 * Unlike the old per-tool `WorkStatusCard` it replaces, this spans the whole chat
 * column (`w-full`) with generous padding and a big glass icon — the artifact's
 * "one BIG moment at a time" rather than a stack of small status rows. `AnimationSlot`
 * is the only caller: it decides which single moment gets this shell on any given
 * render, and remounts it (a fresh `key`) whenever the moment changes so the
 * entrance animation replays.
 */
const MomentShell = ({ image, badgeImage, active, title, children }: Props) => (
  <Box className="mb-3 w-full self-stretch rounded-2xl border border-outline-100 bg-background-0 px-5 py-5 animate-fade-rise motion-reduce:animate-none">
    <HStack space="md" className="items-center">
      <Box className="relative h-16 w-16 shrink-0 items-center justify-center">
        {image ? (
          <Image
            source={image}
            alt=""
            contentFit="contain"
            className={`h-16 w-16 ${active ? "animate-bob motion-reduce:animate-none" : ""}`}
          />
        ) : null}
        {badgeImage ? (
          <Box className="absolute -bottom-1 -right-1 h-7 w-7 items-center justify-center rounded-full border-2 border-background-0 bg-background-50 p-1">
            <Image
              source={badgeImage}
              alt=""
              contentFit="contain"
              className="h-full w-full"
            />
          </Box>
        ) : null}
      </Box>
      <VStack className="flex-1">
        <HStack space="xs" className="items-center">
          {active ? <MontuEmblem size={12} thinking /> : null}
          <Text className="text-2xs font-bold uppercase tracking-wide text-brand-600">
            Montu AI está trabajando
          </Text>
        </HStack>
        <Text
          className={`text-base font-semibold text-typography-800 ${
            active ? "animate-shimmer motion-reduce:animate-none" : ""
          }`}
        >
          {title}
        </Text>
      </VStack>
    </HStack>
    {children}
  </Box>
);

export default MomentShell;
